/**
 * Permissions configuration for role-based access control
 * @format
 */

import { UserRole } from './type';

// Permission constants
export const Permission = {
  // Products
  PRODUCT_READ_ALL: 'product:read:all',
  PRODUCT_READ_PUBLISHED: 'product:read:published',
  PRODUCT_CREATE: 'product:create',
  PRODUCT_UPDATE: 'product:update',
  PRODUCT_DELETE: 'product:delete',
  PRODUCT_PUBLISH: 'product:publish',

  // Categories
  CATEGORY_READ: 'category:read',
  CATEGORY_CREATE: 'category:create',
  CATEGORY_UPDATE: 'category:update',
  CATEGORY_DELETE: 'category:delete',

  // Materials
  MATERIAL_READ: 'material:read',
  MATERIAL_CREATE: 'material:create',
  MATERIAL_UPDATE: 'material:update',
  MATERIAL_DELETE: 'material:delete',

  // Colors
  COLOR_READ: 'color:read',
  COLOR_CREATE: 'color:create',
  COLOR_UPDATE: 'color:update',
  COLOR_DELETE: 'color:delete',

  // Size Guides
  SIZE_GUIDE_READ: 'size-guide:read',
  SIZE_GUIDE_CREATE: 'size-guide:create',
  SIZE_GUIDE_UPDATE: 'size-guide:update',
  SIZE_GUIDE_DELETE: 'size-guide:delete',

  // Sizes
  SIZE_READ: 'size:read',
  SIZE_CREATE: 'size:create',
  SIZE_UPDATE: 'size:update',
  SIZE_DELETE: 'size:delete',

  // Orders
  ORDER_READ: 'order:read',
  ORDER_CREATE: 'order:create',
  ORDER_UPDATE_TO_CONFIRMED: 'order:update:confirmed',
  ORDER_UPDATE_FULFILMENT: 'order:update:fulfilment',
  ORDER_UPDATE_STATUS: 'order:update:status',
  ORDER_VIEW_SHIPPING: 'order:view:shipping',
  ORDER_CREATE_SHIPMENT: 'order:create:shipment',

  // Shipping
  SHIPPING_GET_RATES: 'shipping:get-rates',
  SHIPPING_CREATE_SHIPMENT: 'shipping:create-shipment',

  // Email Campaigns
  CAMPAIGN_VIEW: 'campaign:view',
  CAMPAIGN_CREATE: 'campaign:create',
  CAMPAIGN_SCHEDULE: 'campaign:schedule',
  CAMPAIGN_SEND: 'campaign:send',
  CAMPAIGN_VIEW_ANALYTICS: 'campaign:view:analytics',

  // Newsletter
  NEWSLETTER_VIEW_SUBSCRIBERS: 'newsletter:view:subscribers',
  NEWSLETTER_EXPORT_DATA: 'newsletter:export:data',

  // Metadata
  METADATA_VIEW: 'metadata:view',
  METADATA_CREATE: 'metadata:create',
  METADATA_UPDATE: 'metadata:update',

  //Homepage-Slider
  SLIDER_VIEW: "slider:view",
  SLIDER_UPDATE: "slider:update",
  SLIDER_DELETE:"slider:delete",

  // Merchants
  MERCHANT_VIEW: 'merchant:view',
  MERCHANT_CREATE: 'merchant:create',
  MERCHANT_UPDATE: 'merchant:update',
  MERCHANT_DEACTIVATE: 'merchant:deactivate',
  MERCHANT_DELETE: 'merchant:delete',

  // Users (Watchman) — editing a user's details. Chiefly used to fix a PENDING
  // merchant-admin's email before re-sending the invite. Held by COLLECTIVE_SUPERADMIN,
  // COLLECTIVE_ADMIN and MERCHANT_ADMIN (mirrors watchman USER.UPDATE in seed.ts).
  USER_UPDATE: 'user:update',

  // Audit Logs
  AUDIT_LOG_VIEW: 'audit-log:view',

  // Consent Logs
  CONSENT_LOG_VIEW: 'consent-log:view',

  // Search Analytics
  SEARCH_ANALYTICS_VIEW: 'search-analytics:view',

  // Observability — Microsoft Clarity session recordings (PE-1830).
  // Per-merchant scoped: a holder sees recordings for the active merchant only.
  OBSERVABILITY_SESSION_RECORDINGS_VIEW: 'observability:session-recordings:view',

  // API Keys
  API_KEY_VIEW: 'api-key:view',
  API_KEY_CREATE: 'api-key:create',
  API_KEY_UPDATE: 'api-key:update',
  API_KEY_REVOKE: 'api-key:revoke',

  // Dashboard
  DASHBOARD_VIEW: 'dashboard:view',

  // Invoices (Osprey staff only — COLLECTIVE_SUPERADMIN + COLLECTIVE_ADMIN)
  INVOICE_VIEW: 'invoice:view',
  INVOICE_CREATE: 'invoice:create',
  INVOICE_UPDATE: 'invoice:update',
  INVOICE_VOID: 'invoice:void',

  // Product metadata — attribute schema CRUD (COLLECTIVE_SUPERADMIN only)
  PRODUCT_SCHEMA_MANAGE: 'product-schema:manage',

  // TCS Staff — BPM Officers and Liaisons
  STAFF_VIEW: 'staff:view',
  STAFF_MANAGE: 'staff:manage',

  // Leads — marketing-site contact submissions, waitlist & discovery briefs.
  // Frontend nav gate only; the synergy BFF enforces access server-side via its
  // role-based CollectiveAdminGuard (COLLECTIVE_ADMIN / COLLECTIVE_SUPERADMIN).
  LEADS_VIEW: 'leads:view',

  // Payments finance (PE-1793) — disputes, reconciliation, settlements, payout
  // destinations and refunds. Server-enforced per-permission in osprey-api via the
  // JWT `p` claim (watchman Resource/Action), mirrored here as the frontend nav gate.
  PAYMENT_VIEW: 'payment:view',
  PAYMENT_DISPUTE_VIEW: 'payment-dispute:view',
  PAYMENT_REFUND: 'payment:refund',
  RECONCILIATION_VIEW: 'reconciliation:view',
  RECONCILIATION_RUN: 'reconciliation:run',
  SETTLEMENT_VIEW: 'settlement:view',
  PAYOUT_DESTINATION_VIEW: 'payout-destination:view',
  PAYOUT_DESTINATION_MANAGE: 'payout-destination:manage',

  // Booking services (PE-1875) — bookable services with the "charge customers"
  // toggle + fee. Frontend nav gate only; osprey-api proxies to booking-service
  // and scopes by the admin's selected merchant (x-tenant-id). Visible to both
  // COLLECTIVE (TCS) admins and MERCHANT admins.
  BOOKING_SERVICE_VIEW: 'booking-service:view',
  BOOKING_SERVICE_MANAGE: 'booking-service:manage',

  // Charter (PE-1831) — 5th TCS platform (service/package bookings). Its whole
  // back-office lives in Osprey. Frontend nav gate only; charter-api enforces the
  // real per-resource permissions server-side (SERVICE/PACKAGE/ADDON/BOOKING/ORDER/
  // PORTFOLIO/AVAILABILITY/INTAKE/WAITLIST/MERCHANT) from the JWT `p` claim.
  //   • CHARTER_VIEW   — see the merchant back-office (provider-scoped via x-tenant-id).
  //   • CHARTER_MANAGE — write in the merchant back-office (catalogue + booking/order actions).
  //   • CHARTER_ADMIN  — TCS cross-provider oversight (providers, admin add-ons, read-only bookings/orders/waitlist).
  // ⚠️ Follow-up: add the matching Charter resources/actions to watchman's seed
  // (prisma/seed.ts ROLE_DEFAULTS) — see PR body.
  CHARTER_VIEW: 'charter:view',
  CHARTER_MANAGE: 'charter:manage',
  CHARTER_ADMIN: 'charter:admin',

  // Tenants (tenant model P1) — brand-website tenants over the shared tenant
  // (merchants) table. Mirrors watchman Resource.TENANT actions in seed.ts:
  // view=READ, create=CREATE, update=UPDATE, deactivate=DEACTIVATE, delete=DELETE.
  TENANT_VIEW: 'tenant:view',
  TENANT_CREATE: 'tenant:create',
  TENANT_UPDATE: 'tenant:update',
  TENANT_DEACTIVATE: 'tenant:deactivate',
  TENANT_DELETE: 'tenant:delete',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

// Shared read-only base (no merchant or metadata access)
const COLLECTIVE_USER_PERMISSIONS: Permission[] = [
  Permission.PRODUCT_READ_ALL,
  Permission.CATEGORY_READ,
  Permission.MATERIAL_READ,
  Permission.COLOR_READ,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_READ,
  Permission.ORDER_READ,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.AUDIT_LOG_VIEW,
  Permission.CONSENT_LOG_VIEW,
  Permission.SEARCH_ANALYTICS_VIEW,
  Permission.API_KEY_VIEW,
  Permission.DASHBOARD_VIEW,
  // TENANT_VIEW granted wherever MERCHANT_VIEW/merchant read is granted
  // (mirrors watchman: TENANT.READ alongside MERCHANT.READ).
  Permission.TENANT_VIEW,
];

// Merchant read-only (no merchant nav, no metadata)
const MERCHANT_READ_ONLY_PERMISSIONS: Permission[] = [
  Permission.PRODUCT_READ_ALL,
  Permission.CATEGORY_READ,
  Permission.MATERIAL_READ,
  Permission.COLOR_READ,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_READ,
  Permission.ORDER_READ,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.DASHBOARD_VIEW,
  // Charter (PE-1831) — read-only visibility into the provider's Charter back-office.
  Permission.CHARTER_VIEW,
];

// Osprey admin full permissions (includes merchant management + metadata)
const COLLECTIVE_ADMIN_PERMISSIONS: Permission[] = [
  Permission.MERCHANT_VIEW,
  Permission.MERCHANT_CREATE,
  Permission.MERCHANT_UPDATE,
  Permission.MERCHANT_DEACTIVATE,
  Permission.USER_UPDATE,
  Permission.PRODUCT_READ_ALL,
  Permission.PRODUCT_CREATE,
  Permission.PRODUCT_UPDATE,
  Permission.PRODUCT_DELETE,
  Permission.PRODUCT_PUBLISH,
  Permission.CATEGORY_READ,
  Permission.CATEGORY_CREATE,
  Permission.CATEGORY_UPDATE,
  Permission.CATEGORY_DELETE,
  Permission.MATERIAL_READ,
  Permission.MATERIAL_CREATE,
  Permission.MATERIAL_UPDATE,
  Permission.MATERIAL_DELETE,
  Permission.COLOR_READ,
  Permission.COLOR_CREATE,
  Permission.COLOR_UPDATE,
  Permission.COLOR_DELETE,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_GUIDE_CREATE,
  Permission.SIZE_GUIDE_UPDATE,
  Permission.SIZE_GUIDE_DELETE,
  Permission.SIZE_READ,
  Permission.SIZE_CREATE,
  Permission.SIZE_UPDATE,
  Permission.SIZE_DELETE,
  Permission.ORDER_READ,
  Permission.ORDER_CREATE,
  Permission.ORDER_UPDATE_TO_CONFIRMED,
  Permission.ORDER_UPDATE_FULFILMENT,
  Permission.ORDER_UPDATE_STATUS,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_CREATE,
  Permission.CAMPAIGN_SCHEDULE,
  Permission.CAMPAIGN_SEND,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.NEWSLETTER_EXPORT_DATA,
  Permission.METADATA_VIEW,
  Permission.METADATA_CREATE,
  Permission.METADATA_UPDATE,
  Permission.AUDIT_LOG_VIEW,
  Permission.SEARCH_ANALYTICS_VIEW,
  Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
  Permission.API_KEY_VIEW,
  Permission.API_KEY_CREATE,
  Permission.API_KEY_UPDATE,
  Permission.API_KEY_REVOKE,
  Permission.DASHBOARD_VIEW,
  Permission.SLIDER_VIEW,
  Permission.SLIDER_UPDATE,
  Permission.SLIDER_DELETE,
  Permission.INVOICE_VIEW,
  Permission.INVOICE_CREATE,
  Permission.INVOICE_UPDATE,
  Permission.INVOICE_VOID,
  Permission.STAFF_VIEW,
  Permission.STAFF_MANAGE,
  Permission.LEADS_VIEW,
  // Payments finance (PE-1793)
  Permission.PAYMENT_VIEW,
  Permission.PAYMENT_DISPUTE_VIEW,
  Permission.PAYMENT_REFUND,
  Permission.RECONCILIATION_VIEW,
  Permission.RECONCILIATION_RUN,
  Permission.SETTLEMENT_VIEW,
  Permission.PAYOUT_DESTINATION_VIEW,
  Permission.PAYOUT_DESTINATION_MANAGE,
  // Booking services (PE-1875)
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
  // Charter (PE-1831) — TCS staff manage on providers' behalf + cross-provider oversight.
  Permission.CHARTER_VIEW,
  Permission.CHARTER_MANAGE,
  Permission.CHARTER_ADMIN,
  // Tenants (tenant model P1) — full actions, mirrors watchman
  // COLLECTIVE_ADMIN → TENANT [CREATE, READ, UPDATE, DEACTIVATE, DELETE].
  Permission.TENANT_VIEW,
  Permission.TENANT_CREATE,
  Permission.TENANT_UPDATE,
  Permission.TENANT_DEACTIVATE,
  Permission.TENANT_DELETE,
];

// Merchant admin full permissions (no merchant management, no metadata)
const MERCHANT_ADMIN_PERMISSIONS: Permission[] = [
  Permission.USER_UPDATE,
  Permission.PRODUCT_READ_ALL,
  Permission.PRODUCT_CREATE,
  Permission.PRODUCT_UPDATE,
  Permission.PRODUCT_DELETE,
  Permission.PRODUCT_PUBLISH,
  Permission.CATEGORY_READ,
  Permission.CATEGORY_CREATE,
  Permission.CATEGORY_UPDATE,
  Permission.CATEGORY_DELETE,
  Permission.MATERIAL_READ,
  Permission.MATERIAL_CREATE,
  Permission.MATERIAL_UPDATE,
  Permission.MATERIAL_DELETE,
  Permission.COLOR_READ,
  Permission.COLOR_CREATE,
  Permission.COLOR_UPDATE,
  Permission.COLOR_DELETE,
  Permission.SIZE_GUIDE_READ,
  Permission.SIZE_GUIDE_CREATE,
  Permission.SIZE_GUIDE_UPDATE,
  Permission.SIZE_GUIDE_DELETE,
  Permission.SIZE_READ,
  Permission.SIZE_CREATE,
  Permission.SIZE_UPDATE,
  Permission.SIZE_DELETE,
  Permission.ORDER_READ,
  Permission.ORDER_CREATE,
  Permission.ORDER_UPDATE_TO_CONFIRMED,
  Permission.ORDER_UPDATE_FULFILMENT,
  Permission.ORDER_UPDATE_STATUS,
  Permission.ORDER_VIEW_SHIPPING,
  Permission.CAMPAIGN_VIEW,
  Permission.CAMPAIGN_CREATE,
  Permission.CAMPAIGN_SCHEDULE,
  Permission.CAMPAIGN_SEND,
  Permission.CAMPAIGN_VIEW_ANALYTICS,
  Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
  Permission.NEWSLETTER_EXPORT_DATA,
  // Audit logs — mirrors watchman AUDIT:[READ] for MERCHANT_ADMIN; audit-service
  // scopes results server-side to the admin's own merchant.
  Permission.AUDIT_LOG_VIEW,
  Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
  Permission.DASHBOARD_VIEW,
  Permission.SLIDER_VIEW,
  Permission.SLIDER_UPDATE,
  Permission.SLIDER_DELETE,
  // Booking services (PE-1875)
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
  // Charter (PE-1831) — providers manage their own Charter back-office (provider = merchant).
  Permission.CHARTER_VIEW,
  Permission.CHARTER_MANAGE,
  // View their own one-time invoices and send them to a customer (no create/void — TCS-only).
  // Keep in lockstep with watchman seed: MERCHANT_ADMIN → INVOICE [READ, UPDATE].
  Permission.INVOICE_VIEW,
  Permission.INVOICE_UPDATE,
  // Tenants (tenant model P1) — read-only; mirrors watchman MERCHANT_ADMIN → TENANT [READ].
  Permission.TENANT_VIEW,
];

// Tenant admin (tenant model P1) — administers a brand-website tenant.
// Mirrors watchman seed TENANT_ADMIN → TENANT [READ, UPDATE], USER [CRUD],
// ROLE/PERMISSION/AUDIT/CONTACT [READ]. Notes on the mapping:
//  • USER_UPDATE is osprey's only user-management gate (USER CRUD has no other
//    frontend constants — user management UIs check USER_UPDATE).
//  • CONTACT/ROLE/PERMISSION reads have no osprey Permission constants today;
//    they gate nothing in the UI, so nothing to mirror yet.
//  • BOOKING_SERVICE_* is frontend-gate-only vocabulary (no watchman Resource) —
//    granted here per the tenant design so brand sites can manage bookings.
const TENANT_ADMIN_PERMISSIONS: Permission[] = [
  Permission.TENANT_VIEW,
  Permission.TENANT_UPDATE,
  Permission.USER_UPDATE,
  Permission.AUDIT_LOG_VIEW,
  Permission.BOOKING_SERVICE_VIEW,
  Permission.BOOKING_SERVICE_MANAGE,
];

// Tenant user (tenant model P1) — read-only within own brand-website tenant.
// Mirrors watchman seed TENANT_USER → TENANT/USER/CONTACT [READ].
const TENANT_USER_PERMISSIONS: Permission[] = [
  Permission.TENANT_VIEW,
];

// Role to permissions mapping (Watchman IAM roles)
export const RolePermissions: Record<UserRole, Permission[]> = {
  // Governance read-only + merchant view + metadata + merchant creation
  [UserRole.COLLECTIVE_SUPERADMIN]: [
    Permission.MERCHANT_VIEW,
    Permission.MERCHANT_CREATE,
    Permission.MERCHANT_UPDATE,
    Permission.MERCHANT_DELETE,
    Permission.MERCHANT_DEACTIVATE,
    Permission.USER_UPDATE,
    Permission.PRODUCT_READ_ALL,
    Permission.CATEGORY_READ,
    Permission.MATERIAL_READ,
    Permission.COLOR_READ,
    Permission.SIZE_GUIDE_READ,
    Permission.SIZE_READ,
    Permission.ORDER_READ,
    Permission.ORDER_VIEW_SHIPPING,
    Permission.CAMPAIGN_VIEW,
    Permission.CAMPAIGN_VIEW_ANALYTICS,
    Permission.NEWSLETTER_VIEW_SUBSCRIBERS,
    Permission.METADATA_VIEW,
    Permission.AUDIT_LOG_VIEW,
    Permission.CONSENT_LOG_VIEW,
    Permission.SEARCH_ANALYTICS_VIEW,
    Permission.OBSERVABILITY_SESSION_RECORDINGS_VIEW,
    Permission.API_KEY_VIEW,
    Permission.API_KEY_CREATE,
    Permission.API_KEY_REVOKE,
    Permission.DASHBOARD_VIEW,
    Permission.SLIDER_VIEW,
    Permission.INVOICE_VIEW,
    Permission.INVOICE_CREATE,
    Permission.INVOICE_UPDATE,
    Permission.INVOICE_VOID,
    Permission.PRODUCT_SCHEMA_MANAGE,
    Permission.STAFF_VIEW,
    Permission.STAFF_MANAGE,
    Permission.LEADS_VIEW,
    // Payments finance (PE-1793)
    Permission.PAYMENT_VIEW,
    Permission.PAYMENT_DISPUTE_VIEW,
    Permission.PAYMENT_REFUND,
    Permission.RECONCILIATION_VIEW,
    Permission.RECONCILIATION_RUN,
    Permission.SETTLEMENT_VIEW,
    Permission.PAYOUT_DESTINATION_VIEW,
    Permission.PAYOUT_DESTINATION_MANAGE,
    // Booking services (PE-1875) — view only for superadmin governance posture
    Permission.BOOKING_SERVICE_VIEW,
    // Charter (PE-1831) — governance visibility + cross-provider oversight.
    Permission.CHARTER_VIEW,
    Permission.CHARTER_MANAGE,
    Permission.CHARTER_ADMIN,
    // Tenants (tenant model P1) — full actions, mirrors watchman
    // COLLECTIVE_SUPERADMIN → TENANT [CREATE, READ, UPDATE, DEACTIVATE, DELETE].
    Permission.TENANT_VIEW,
    Permission.TENANT_CREATE,
    Permission.TENANT_UPDATE,
    Permission.TENANT_DEACTIVATE,
    Permission.TENANT_DELETE,
  ],

  // Full read/write across all merchants
  [UserRole.COLLECTIVE_ADMIN]: COLLECTIVE_ADMIN_PERMISSIONS,

  // Read-only across products and orders only (no merchants, no metadata)
  [UserRole.COLLECTIVE_USER]: COLLECTIVE_USER_PERMISSIONS,

  // Full read/write within own merchant (no merchant nav, no metadata)
  [UserRole.MERCHANT_ADMIN]: MERCHANT_ADMIN_PERMISSIONS,

  // Read-only within own merchant (no merchant nav, no metadata)
  [UserRole.MERCHANT_USER]: MERCHANT_READ_ONLY_PERMISSIONS,

  // Brand-website tenant admin (tenant model P1)
  [UserRole.TENANT_ADMIN]: TENANT_ADMIN_PERMISSIONS,

  // Read-only within own brand-website tenant (tenant model P1)
  [UserRole.TENANT_USER]: TENANT_USER_PERMISSIONS,
};

/**
 * Get the default route for a given role
 */
export const getDefaultRouteForRole = (role: UserRole | undefined): string => {
  if (!role) return '/login';
  switch (role) {
    case UserRole.COLLECTIVE_SUPERADMIN:
    case UserRole.COLLECTIVE_ADMIN:
    case UserRole.COLLECTIVE_USER:
    case UserRole.MERCHANT_ADMIN:
    case UserRole.MERCHANT_USER:
    case UserRole.TENANT_ADMIN:
    case UserRole.TENANT_USER:
      return '/';
    default:
      return '/no-access';
  }
};

/**
 * Check if a role has a specific permission
 */
export const roleHasPermission = (
  role: UserRole | undefined,
  permission: Permission
): boolean => {
  if (!role) return false;
  const permissions = RolePermissions[role];
  return permissions?.includes(permission) ?? false;
};

/**
 * Check if a role has any of the specified permissions
 */
export const roleHasAnyPermission = (
  role: UserRole | undefined,
  permissions: Permission[]
): boolean => {
  if (!role) return false;
  return permissions.some((p) => roleHasPermission(role, p));
};

/**
 * Check if a role has all of the specified permissions
 */
export const roleHasAllPermissions = (
  role: UserRole | undefined,
  permissions: Permission[]
): boolean => {
  if (!role) return false;
  return permissions.every((p) => roleHasPermission(role, p));
};

/**
 * Check if a role can access draft products
 */
export const roleCanAccessDrafts = (role: UserRole | undefined): boolean => {
  return roleHasPermission(role, Permission.PRODUCT_READ_ALL);
};
